<template>
  <div class="assigned-tasks">
    <div class="page-header">
      <h2>{{ t('task.assignedTasks') }}</h2>
    </div>

    <TaskInboxTabs
      v-model="activeTab"
      mode="assigned"
      :counts="badgeCounts"
    />

    <div class="stats-cards">
      <a-row :gutter="16">
        <a-col :xs="12" :sm="8" :md="4" v-for="card in statCards" :key="card.key">
          <a-card size="small">
            <a-statistic
              :title="card.title"
              :value="card.value"
              :value-style="{ color: card.color }"
            />
          </a-card>
        </a-col>
      </a-row>
    </div>

    <a-card class="search-card">
      <a-form layout="inline" :model="filters" @finish="handleSearch">
        <a-form-item>
          <a-input
            v-model:value="filters.keyword"
            :placeholder="t('task.searchByWorkItemId')"
            style="width: 200px"
            allow-clear
          >
            <template #prefix><SearchOutlined /></template>
          </a-input>
        </a-form-item>

        <a-form-item>
          <a-space>
            <a-button type="primary" html-type="submit">{{ t('task.search') }}</a-button>
            <a-button @click="resetFilters">{{ t('task.reset') }}</a-button>
            <a-button :loading="loading" @click="refreshTasks">
              <template #icon><ReloadOutlined /></template>
              {{ t('task.refresh') }}
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card :bordered="false">
      <a-alert
        v-if="loadError"
        type="error"
        show-icon
        :message="t('task.loadFailed')"
        :description="loadError"
        style="margin-bottom: 16px"
      >
        <template #action>
          <a-button size="small" @click="refreshTasks">{{ t('task.retry') }}</a-button>
        </template>
      </a-alert>

      <a-empty
        v-if="!loading && !loadError && tasks.length === 0"
        :description="t('task.noTasks')"
      />

      <a-table
        v-else
        :columns="columns"
        :data-source="tasks"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'task_info'">
            <div class="task-info">
              <div class="task-title">
                <a @click="viewTaskDetail(record)">{{ getDisplayTitle(record) }}</a>
                <a-tag v-if="record.round > 1" color="purple">
                  {{ t('task.roundBadge', { n: record.round }) }}
                </a-tag>
              </div>
              <div class="task-meta">
                <span>{{ t('task.workItemId') }}: {{ record.work_item_id }}</span>
                <span>·</span>
                <span>{{ t(`task.${getTaskPurposeI18nKey(String(record.task_purpose))}`) }}</span>
              </div>
            </div>
          </template>

          <template v-else-if="column.key === 'assignee'">
            <span v-if="record.assignee_id || record.assignee">
              {{ formatTaskUserLabel(record.assignee, record.assignee_id) }}
            </span>
            <span v-else class="text-muted">{{ t('task.unassigned') }}</span>
          </template>

          <template v-else-if="column.key === 'status'">
            <a-tag :color="getTaskStatusColor(String(record.status))">
              {{ t(`task.${getTaskStatusI18nKey(String(record.status))}`) }}
            </a-tag>
            <div
              v-if="record.status === TaskStatus.REJECTED_AT_ASSIGNMENT && record.rejection_reason"
              class="rejection-reason"
            >
              {{ t('task.rejectionReason') }}: {{ record.rejection_reason }}
            </div>
          </template>

          <template v-else-if="column.key === 'created_at'">
            {{ formatDateTime(record.created_at) }}
          </template>

          <template v-else-if="column.key === 'action'">
            <a-space wrap>
              <a-button
                v-if="record.status === TaskStatus.SUBMITTED"
                type="primary"
                size="small"
                :loading="actionLoading[record.id]"
                @click="handleApprove(record)"
              >
                {{ t('task.completeTaskAction') }}
              </a-button>
              <a-button
                v-if="record.status === TaskStatus.SUBMITTED"
                size="small"
                :loading="actionLoading[record.id]"
                @click="openReviseModal(record)"
              >
                {{ t('task.continueReviewAction') }}
              </a-button>
              <a-button size="small" @click="viewTaskDetail(record)">
                {{ t('task.details') }}
              </a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-drawer
      v-model:open="taskDetailVisible"
      :title="t('task.taskDetail')"
      width="640"
      :footer-style="{ textAlign: 'right' }"
      @close="closeDetail"
    >
      <TaskDetail
        v-if="selectedTask"
        :task="selectedTask"
        role="creator"
        @update="handleTaskUpdate"
        @close="closeDetail"
      />
      <template #footer>
        <a-space>
          <a-button @click="closeDetail">{{ t('task.close') }}</a-button>
          <a-button
            v-if="selectedTask?.status === TaskStatus.SUBMITTED"
            type="primary"
            :loading="selectedTask ? actionLoading[selectedTask.id] : false"
            @click="selectedTask && handleApprove(selectedTask)"
          >
            {{ t('task.completeTaskAction') }}
          </a-button>
          <a-button
            v-if="selectedTask?.status === TaskStatus.SUBMITTED"
            :loading="selectedTask ? actionLoading[selectedTask.id] : false"
            @click="selectedTask && openReviseModal(selectedTask)"
          >
            {{ t('task.continueReviewAction') }}
          </a-button>
        </a-space>
      </template>
    </a-drawer>

    <a-modal
      v-model:open="reviseModalVisible"
      :title="t('task.continueReviewAction')"
      :confirm-loading="revisingTask ? actionLoading[revisingTask.id] : false"
      @ok="handleRevise"
    >
      <a-form layout="vertical">
        <a-form-item :label="t('task.reviseComment')" required>
          <a-textarea
            v-model:value="reviseComment"
            :placeholder="t('task.enterReviseComment')"
            :rows="4"
            :maxlength="1000"
            show-count
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons-vue'
import dayjs from 'dayjs'

import { TaskStatus } from '@/api/common-types'
import { useDrawerManager } from '@/composables/useDrawerManager'
import {
  aggregateTaskStatusCounts,
  approveTask as approveTaskApi,
  getAssignedTasks,
  reviseTask as reviseTaskApi,
  type TaskQueryParams,
  type TaskResponse,
} from '@/modules/task/api'
import { runApiBatch } from '@/shared/api/use-api-batch'
import TaskDetail from '@/modules/task/components/TaskDetail.vue'
import TaskInboxTabs from '@/modules/task/components/TaskInboxTabs.vue'
import { useInboxTabBadgeAck } from '@/modules/task/composables/useInboxTabBadgeAck'
import {
  buildInboxListPage,
  countByInboxTab,
  formatTaskUserLabel,
  getTaskDisplayTitle,
  getTaskPurposeI18nKey,
  getTaskStatusColor,
  getTaskStatusI18nKey,
  inboxListQueryFlags,
  parseWorkItemIdKeyword,
} from '@/modules/task/utils/task-display'

const DRAWER_ID = 'assigned-task-detail-drawer'

const { t } = useI18n()
const drawerManager = useDrawerManager()

const loading = ref(false)
const loadError = ref<string | null>(null)
const tasks = ref<TaskResponse[]>([])
const allTasksForCounts = ref<TaskResponse[]>([])
const activeTab = ref('my_review')
const tabCounts = computed(() => countByInboxTab('assigned', allTasksForCounts.value))
const { badgeCounts, acknowledgeTabBadges } = useInboxTabBadgeAck('assigned', tabCounts)
/** 仅「进入页面」首次拉计数时确认角标；页内刷新保留增量提示 */
const pendingBadgeAck = ref(true)
const taskStats = ref<Record<string, number>>({
  total: 0,
  pending: 0,
  processing: 0,
  submitted: 0,
  completed: 0,
  rejected_at_assignment: 0,
})
const taskDetailVisible = ref(false)
const selectedTask = ref<TaskResponse | null>(null)
const reviseModalVisible = ref(false)
const revisingTask = ref<TaskResponse | null>(null)
const reviseComment = ref('')
const actionLoading = ref<Record<number, boolean>>({})

const filters = reactive<TaskQueryParams>({
  status: undefined,
  keyword: '',
})

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number) => t('task.totalRecords', { total }),
  onChange: (page: number, pageSize: number) => {
    pagination.current = page
    pagination.pageSize = pageSize
    void fetchTasks()
  },
  onShowSizeChange: (_current: number, size: number) => {
    pagination.current = 1
    pagination.pageSize = size
    void fetchTasks()
  },
})

watch(activeTab, () => {
  pagination.current = 1
  void fetchTasks()
})

const columns = computed(() => [
  { title: t('task.taskInfo'), key: 'task_info', ellipsis: true },
  { title: t('task.assignee'), key: 'assignee', width: 140 },
  { title: t('task.status'), key: 'status', width: 180 },
  { title: t('task.createTime'), key: 'created_at', width: 170 },
  { title: t('task.actions'), key: 'action', width: 240, fixed: 'right' as const },
])

const statCards = computed(() => [
  { key: 'total', title: t('task.totalTasks'), value: taskStats.value.total ?? 0, color: '#1890ff' },
  { key: 'pending', title: t('task.pending'), value: taskStats.value.pending ?? 0, color: '#faad14' },
  { key: 'processing', title: t('task.processing'), value: taskStats.value.processing ?? 0, color: '#52c41a' },
  { key: 'submitted', title: t('task.submitted'), value: taskStats.value.submitted ?? 0, color: '#13c2c2' },
  { key: 'completed', title: t('task.completed'), value: taskStats.value.completed ?? 0, color: '#722ed1' },
  {
    key: 'rejected',
    title: t('task.rejectedAtAssignment'),
    value: taskStats.value.rejected_at_assignment ?? 0,
    color: '#ff4d4f',
  },
])

onMounted(() => {
  drawerManager?.registerDrawer(DRAWER_ID, closeDetail)
  pendingBadgeAck.value = true
  void refreshTasks()
})

onUnmounted(() => {
  drawerManager?.unregisterDrawer(DRAWER_ID)
})

async function fetchTasks() {
  await refreshTasks()
}

async function refreshTasks() {
  loading.value = true
  loadError.value = null
  try {
    const workItemId = parseWorkItemIdKeyword(filters.keyword)
    const { apiStatus, needClientFilter } = inboxListQueryFlags(
      'assigned',
      activeTab.value,
      workItemId,
    )

    const results = await runApiBatch([
      (ctx) =>
        getAssignedTasks(
          {
            status: apiStatus,
            page: needClientFilter ? 1 : pagination.current,
            page_size: needClientFilter ? 100 : pagination.pageSize,
          },
          ctx.requestConfig,
        ),
      (ctx) =>
        getAssignedTasks({ page: 1, page_size: 100 }, ctx.requestConfig),
    ])

    const listResult = results[0]
    const statsResult = results[1]

    if (listResult.status === 'fulfilled') {
      const page = buildInboxListPage({
        mode: 'assigned',
        tab: activeTab.value,
        items: listResult.value.items ?? [],
        workItemId,
        page: pagination.current,
        pageSize: pagination.pageSize,
        needClientFilter,
        serverTotal: listResult.value.total,
      })
      tasks.value = page.items
      pagination.total = page.total
    } else {
      tasks.value = []
      pagination.total = 0
      loadError.value = t('task.getTaskListFailed')
      console.error('获取指派的任务失败:', listResult.reason)
    }

    if (statsResult.status === 'fulfilled') {
      const all = statsResult.value.items ?? []
      allTasksForCounts.value = all
      taskStats.value = aggregateTaskStatusCounts(all)
      if (pendingBadgeAck.value) {
        acknowledgeTabBadges()
        pendingBadgeAck.value = false
      }
    }
  } catch (error) {
    console.error('获取指派的任务失败:', error)
    tasks.value = []
    pagination.total = 0
    loadError.value = t('task.getTaskListFailed')
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  pagination.current = 1
  void fetchTasks()
}

function resetFilters() {
  filters.keyword = ''
  activeTab.value = 'my_review'
  handleSearch()
}

function getDisplayTitle(task: TaskResponse) {
  return getTaskDisplayTitle(task, t('task.taskFallbackTitle', { id: task.id }))
}

function formatDateTime(value?: string | null) {
  if (!value) return '—'
  return dayjs(value).format('YYYY-MM-DD HH:mm')
}

async function handleApprove(task: TaskResponse) {
  actionLoading.value[task.id] = true
  try {
    await approveTaskApi(task.id)
    message.success(t('task.taskApproved'))
    await refreshTasks()
  } catch (error) {
    console.error('通过任务失败:', error)
  } finally {
    actionLoading.value[task.id] = false
  }
}

function openReviseModal(task: TaskResponse) {
  revisingTask.value = task
  reviseComment.value = ''
  reviseModalVisible.value = true
}

async function handleRevise() {
  if (!revisingTask.value) return
  if (!reviseComment.value.trim()) {
    message.warning(t('task.pleaseEnterReviseComment'))
    return
  }
  actionLoading.value[revisingTask.value.id] = true
  try {
    await reviseTaskApi(revisingTask.value.id, reviseComment.value.trim())
    message.success(t('task.taskRevised'))
    reviseModalVisible.value = false
    await refreshTasks()
  } catch (error) {
    console.error('打回任务失败:', error)
  } finally {
    actionLoading.value[revisingTask.value.id] = false
  }
}

function viewTaskDetail(task: TaskResponse) {
  selectedTask.value = task
  drawerManager?.openDrawer(DRAWER_ID)
  taskDetailVisible.value = true
}

function closeDetail() {
  taskDetailVisible.value = false
}

async function handleTaskUpdate() {
  await refreshTasks()
}
</script>

<style lang="less" scoped>
.assigned-tasks {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
}

.stats-cards {
  margin-bottom: 24px;
}

.search-card {
  margin-bottom: 24px;
}

.task-info {
  .task-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    a {
      font-weight: 500;
      color: #1890ff;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .task-meta {
    font-size: 12px;
    color: #999;
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
}

.rejection-reason {
  margin-top: 4px;
  font-size: 12px;
  color: #ff4d4f;
}

.text-muted {
  color: #999;
}

:deep(.ant-table-tbody > tr > td) {
  padding: 12px 16px;
}
</style>
